import React, { useCallback, ComponentType, MouseEvent, useContext, useMemo, HTMLAttributes, useEffect, useState, ButtonHTMLAttributes } from 'react';
import { styled } from '@mui/material';
import t from '@/utils/translate';
import { ModelData } from '@/state/types/model-data';
import { ProjectData } from '@/state/types/project-data';
import { reprocess as modelsReprocess } from '@/actions/model';
import BulkOperationButton from '@/components/BulkOperationButton';
import hinted from '@/components/Hinted';
import ReprocessIcon from '@/components/icons/Reprocess';
import BackgroundContext from '@/components/background/BackgroundContext';
import { Button } from '@/components';
import DownloadIcon from '@/components/icons/Download';
import { AmountsLabel } from '@/components/AmountLabel';
import { CircularProgress } from '@/components';
import RoundedCross from '@/components/icons/RoundedCross';
import { runBulk } from '@/actions/utils';
import TrashBasket from '@/components/icons/TrashBasket';
import BoldSpeсialCross from '@/components/icons/BoldSpeсialCross';

export const ModelActionButton = hinted(
  styled(Button)(() => ({
    width: '24px',
    height: '24px',
    minWidth: '24px',
    padding: '2px',
    '& svg': {
      opacity: 0.5,
    },
    '&:hover': {
      backgroundColor: 'unset',
    },
    ['&:hover svg']: {
      opacity: 1,
    },
    ['&:not(:first-of-type)']: {
      marginLeft: '12px',
    },
  })),
  { distance: 1 }
);

type BulkButtonProps = {
  project: ProjectData;
  items: Array<ModelData>;
};

type ReprocessButtonProps = {
  project: ProjectData;
  items: Array<ModelData> | ModelData;
}

const withReprocessOnClick = function <
  ElementType extends HTMLElement,
  T extends {
    onClick: (event: MouseEvent<ElementType>) => void | Promise<void>;
  } & ReprocessButtonProps
>(
  Component: ComponentType<T>
): ComponentType<Omit<T, 'onClick'> & ReprocessButtonProps> {
  return (props) => {
    const handleClick = useCallback((_event: React.MouseEvent<ElementType>) => {
      const items: Array<ModelData> = Array.isArray(props.items)
        ? props.items
        : [ props.items ];
      const { ids, reprocessCount } = items.reduce((acc: { ids: number[], reprocessCount: number }, item: ModelData) => {
        acc.ids.push(item.id);
        acc.reprocessCount += Number(!item.ready && !item.failed);
        return acc;
      }, { ids: [], reprocessCount: 0 });

      let confirmation = false;
      if (!reprocessCount) {
        confirmation = confirm(t('model.title.confirmReprocess', { count: ids.length }));
      } else if (reprocessCount === ids.length) {
        confirmation = confirm(t('model.title.confirmRepeatedReprocess', { count: ids.length }));
      } else {
        confirmation = confirm(t('model.title.confirmRepeatedReprocessForSome'));
      }

      if(confirmation) {
        modelsReprocess(props.project.code, ids);
      }

    }, [ props.project, props.items ]);

    const componentProps = {
      ...(props as unknown as Omit<T, 'onClick'>),
      onClick: handleClick,
    } as T;
    return <Component {...componentProps}/>;
  };
};

export const BulkReprocessButton = withReprocessOnClick(({ onClick, items }) => {
  items = useMemo(
    () => (Array.isArray(items) ? items : [ items ]),
    [ items]
  );
  return <BulkOperationButton onClick={onClick} disabled={!items.length}>
    <ReprocessIcon size={20} color='#8777D9'/>
    {t('model.action.bulk.reprocess')}
    {!!items.length && <AmountsLabel>{items.length}</AmountsLabel>}
  </BulkOperationButton>
});

const ReprocessButtonView = hinted(
  styled('button')({
    width: '24px',
    height: '24px',
    border: 'none',
    background: 'none',
    fontSize: '20px',
    padding: '2px',
    color: '#C0B6F2',
    '&:hover': {
      color: '#8777D9',
    },
  }),
  {}
);

export const ReprocessButton = withReprocessOnClick(({ onClick }) => (
  <ReprocessButtonView hint={t('model.action.reprocess')} onClick={onClick}>
    <ReprocessIcon/>
  </ReprocessButtonView>
));

type ModelDeleteButtonProps = {
  project: ProjectData;
  item: ModelData;
  onAction: (models: ModelData[]) => void;
};

export const DeleteButton = ({ project, item, onAction }: ModelDeleteButtonProps) => {
  const background = useContext(BackgroundContext);

  const startDeletion = useCallback(() => {
    const progress = background.modelDownloadProgress(project.code, item.id);
    if (typeof progress === 'number') {
      background.abortModelDownload(project.code, item.id);
    }
    onAction([item]);
  }, [project.code, item.id, onAction, item])

  return (
    <ModelActionButton hint={t('model.action.delete')} onClick={startDeletion}>
      <TrashBasket size={20} color="#DE350B"/>
    </ModelActionButton>
  );
};

type BulkModelDeleteButtonProps = BulkButtonProps & {
  onAction: (models: ModelData[]) => void;
};

export const BulkDeleteButton = ({ project, items, onAction }: BulkModelDeleteButtonProps) => {
  const background = useContext(BackgroundContext);

  const startDeletion = useCallback(() => {
    for (const item of items) {
      const progress = background.modelDownloadProgress(project.code, item.id);
      if (typeof progress === 'number') {
        background.abortModelDownload(project.code, item.id);
      }
    }
    onAction(Array.from(items));
  }, [project.code, items, onAction ])

  return (
    <BulkOperationButton onClick={startDeletion} disabled={!items.length}>
      <TrashBasket size={20} color="#DE350B"/>
      {t('model.action.bulk.delete')}
      {!!items.length && <AmountsLabel>{items.length}</AmountsLabel>}
    </BulkOperationButton>
  );
};

export const BulkDownloadButton = ({ project, items }: BulkButtonProps) => {
  const background = useContext(BackgroundContext);

  const [ candidates, setCandidates ] = useState<number[]>([]);

  const start = useCallback(() => runBulk(
    candidates,
    (id) => background.modelDownload(project.code, id),
    (e) => console.error(e)
  ), [ project.code, candidates ]);

  useEffect(() => {
    const handler = () => {
      const c = new Set<number>();
      for (const { id } of items) {
        const progress = background.modelDownloadProgress(project.code, id);
        if (typeof progress !== 'number') {
          c.add(id);
        }
      }
      setCandidates(Array.from(c));
    };
    background.addEventListener('downloadProgress', handler);

    handler();

    return () => {
      background.removeEventListener('downloadProgress', handler);
    }
  }, [ project.code, items ]);

  return (
    <BulkOperationButton onClick={start} disabled={!candidates.length}>
      <DownloadIcon size={20} color="#091E42"/>
      {t('model.action.bulk.download')}
      {!!candidates.length && <AmountsLabel>{candidates.length}</AmountsLabel>}
    </BulkOperationButton>
  );
};

const DownloadProgress = styled(CircularProgress)({
  width: '20px',
  height: '20px',
  paddingTop: '3px',
});

export const BulkAbortDownloadingButton = ({ project, items }: BulkButtonProps) => {
  const background = useContext(BackgroundContext);

  const [ candidates, setCandidates ] = useState<number[]>([]);
  const [ progress, setProgress ] = useState<number | null>();

  const abort = useCallback(async () => runBulk(
    candidates,
    (id) => background.abortModelDownload(project.code, id),
    (e) => console.error(e)
  ), [ project.code, candidates ]);

  useEffect(() => {
    const handler = () => {
      const c = new Set<number>();
      let total = 0, loaded = 0;
      const downloads = background.downloads.get(project.code);
      if (downloads) {
        const ids = new Set(items.map((i) => i.id));
        for (const d of downloads) {
          if (ids.has(d.id)) {
            c.add(d.id);
            total += d.total || d.loaded;
            loaded += d.loaded;
          }
        }
      }
      setCandidates(Array.from(c));
      setProgress(total ? Math.round(loaded * 100 / total) : undefined);
    };
    background.addEventListener('downloadProgress', handler);

    handler();

    return () => {
      background.removeEventListener('downloadProgress', handler);
    }
  }, [ project.code, items ]);

  return (
    <BulkOperationButton onClick={abort} disabled={!candidates.length}>
      {!!candidates.length
        ? <div style={{ position: 'relative', display: 'flex' }}>
            <DownloadProgress
              variant="determinate"
              value={progress || 0}
              size={18}
              disableBackground={true}
              sx={{
                padding: 0,
                circle: { color: '#6B778C' }
              }}
            />
            <BoldSpeсialCross color="#DE350B" size={8} style={{ position: 'absolute', top: '6px', left: '6px' }}/>
        </div>
        : <RoundedCross color="#DE350B" size={18} style={{ margin: '1px' }}/>
      }
      {t('model.action.bulk.abortDownloading')}
      {!!candidates.length && <AmountsLabel>{candidates.length}</AmountsLabel>}
    </BulkOperationButton>
  );
};
